import Button from '@/components/UI/Button';
import { hasOwnProperty } from '@/helpers';
import { _isEmpty, _isNotEmpty } from '@/littledash';
import { Animal } from '@/model/Animal.model';
import { Measurement } from '@/model/Measurement.model';
import type { Study } from '@/model/Study.model';
import * as Auth from '@/support/auth';
import { isClosed } from '@/support/study';
import { sortDescByDateStrProperty } from '@/utils/array.utils';
import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import Graph from './Graph';
import MeasurementList from './MeasurementList/MeasurementList';
import type { AddMeasurementProps } from '@/components/Modals/AddMeasurement/AddMeasurement';

const READ_ONLY_MEASUREMENT_IDS = ['animal_food_intake', 'animal_fluid_intake'];

interface MeasurementsProps {
  fromWorkflow: boolean;
  measurements: Array<Measurement>;
  onMeasurementSubmit: (response: any, editing: boolean) => Promise<void>;
  study: Study;
  subject: Animal;
}

const Measurements: FC<MeasurementsProps> = ({
  study,
  subject,
  onMeasurementSubmit,
  measurements = [],
  fromWorkflow,
}) => {
  const calculations = study?.settings?.calculations || [];
  const [activeMeasurement, setActiveMeasurement] = useState(calculations[0]);
  const dispatch = useDispatch();

  const closeModal = () => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  };

  const openModal = (props: AddMeasurementProps) => {
    dispatch({
      type: 'OPEN_MODAL',
      modal: 'ADD_MEASUREMENT',
      props,
    });
  };

  const orderedMeasurements = measurements.sort(sortDescByDateStrProperty('measured_at'));
  const filteredMeasurements = orderedMeasurements.filter((m) =>
    hasOwnProperty(m.variables ?? {}, activeMeasurement.id ?? null)
  );

  const hasWriteAccess = study && Auth.isWriteUserForStudy(study);
  const canAddSelectedType =
    hasWriteAccess && !fromWorkflow && activeMeasurement && !READ_ONLY_MEASUREMENT_IDS.includes(activeMeasurement.id);

  const NoMeasurements = ({ title }: { title: string }) => (
    <div className="pa4 br3 bg-near-white">
      <div className="tc">
        <h3 className="lh-title normal f5 pb2">{title}</h3>
      </div>
    </div>
  );

  if (_isEmpty(calculations)) {
    return (
      <div className="ma3">
        <NoMeasurements title="No preset has been selected." />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center pa3">
        <Select
          className="ui__select w5"
          classNamePrefix="ui__select"
          onChange={(result) => result && setActiveMeasurement(result.value)}
          value={{
            value: activeMeasurement,
            label: activeMeasurement.name,
          }}
          options={calculations.map((calculation) => ({
            value: calculation,
            label: calculation.name,
          }))}
        />
        {hasWriteAccess && !isClosed(study) && canAddSelectedType && !subject.terminated_at && (
          <Button
            onClick={() => {
              openModal({
                studyApiId: study.api_id,
                subject,
                preset: study.settings,
                settings: {
                  measuring: [activeMeasurement.id],
                },
                closeModal,
                onMeasurementSubmit,
              });
            }}
            className={`f6 basier-reg pl0 stateless blue`}
          >
            Add new
          </Button>
        )}
      </div>

      {_isNotEmpty(filteredMeasurements) ? (
        <React.Fragment>
          <div className="mb4 ph3">
            <Graph
              measurements={orderedMeasurements}
              activeMeasurement={activeMeasurement.id}
              unit={activeMeasurement.unit}
            />
          </div>
          <div className="bt bb b--moon-gray" data-test-key="measurements-list">
            <MeasurementList
              preset={study.settings}
              canEdit={hasWriteAccess}
              studyApiId={study.api_id}
              subject={subject}
              measurements={measurements}
              activeCalculation={activeMeasurement}
              // @ts-expect-error: Type mismatch in onMeasurementSubmit
              handleCallback={onMeasurementSubmit}
              readOnlyMeasurementIds={READ_ONLY_MEASUREMENT_IDS}
            />
          </div>
        </React.Fragment>
      ) : (
        <div className="mh3" data-testid="animal-no-measurements">
          <NoMeasurements title="No measurements of this type have been added yet." />
        </div>
      )}
    </div>
  );
};

export default Measurements;
